import { TranslationService } from "../services/TranslationService";
import { ObjetTouristique } from "../types/apidae";
import type { WidgetLanguage } from "../types/widgets.d";

type IllustrationLike = {
  traductionFichiers?: Array<{
    locale?: string;
    url?: string;
    urlListe?: string;
    urlFiche?: string;
    urlDiaporama?: string;
  }>;
};

/**
 * Détecte l'environnement basé sur le hostname de la page
 * et retourne l'URL d'API correspondante
 */
export function getApiUrlFromEnvironment(): string {
  // Détecter l'environnement depuis le hostname
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  // Mapping des environnements vers les URLs d'API
  const environmentApiUrls: Record<string, string> = {
    // Production
    "apidae-tourisme.com": "https://api.apidae-tourisme.com",
    // Preprod
    "apidae-tourisme.dev": "https://api.apidae-tourisme.dev",
    // Cooking
    "apidae-tourisme.cooking": "https://api.apidae-tourisme.cooking",
    // Local
    localhost: "http://api.apidae-tourisme.home",
    "127.0.0.1": "http://api.apidae-tourisme.home",
  };

  // Vérifier si le hostname correspond exactement à un mapping
  if (hostname && environmentApiUrls[hostname]) {
    return environmentApiUrls[hostname];
  }

  // Vérifier si le hostname contient un sous-domaine d'environnement
  for (const [envHostname, apiUrl] of Object.entries(environmentApiUrls)) {
    if (
      hostname.includes(envHostname) ||
      hostname.endsWith(`.${envHostname}`)
    ) {
      return apiUrl;
    }
  }

  // Par défaut, utiliser l'URL de production
  return "https://api.apidae-tourisme.com";
}

/**
 * Détecte l'environnement basé sur le hostname de la page
 * et retourne l'URL d'Elloha correspondante
 */
export function getEllohaUrlFromEnvironment(): string {
  // Détecter l'environnement depuis le hostname
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  // Mapping des environnements vers les URLs d'Elloha
  const environmentEllohaUrls: Record<string, string> = {
    // Production
    "apidae-tourisme.com": "https://elloha.apidae-tourisme.com",
    // Preprod
    "apidae-tourisme.dev": "https://elloha.apidae-tourisme.dev",
    // Cooking
    "apidae-tourisme.cooking": "https://elloha.apidae-tourisme.cooking",
    // Local
    localhost: "http://elloha.apidae-tourisme.home",
    "127.0.0.1": "http://elloha.apidae-tourisme.home",
  };

  // Vérifier si le hostname correspond exactement à un mapping
  if (hostname && environmentEllohaUrls[hostname]) {
    return environmentEllohaUrls[hostname];
  }

  // Vérifier si le hostname contient un sous-domaine d'environnement
  for (const [envHostname, ellohaUrl] of Object.entries(
    environmentEllohaUrls,
  )) {
    if (
      hostname.includes(envHostname) ||
      hostname.endsWith(`.${envHostname}`)
    ) {
      return ellohaUrl;
    }
  }

  // Par défaut, utiliser l'URL de production
  return "https://elloha.apidae-tourisme.com";
}

/**
 * Mapping `WidgetLanguage` -> `culture` attendu par Elloha.
 *
 * En allant sur une page Elloha (https://reservation.elloha.com/Search/Index/7617ac8b-2e82-49d9-92a5-8a28771c90d4?idoi=ba725ed7-50ee-47ec-9c5e-34032e4c18e7&dateFrom=27/02/2026&NbAdultes=1&culture=fr-FR&currency=EUR), on peut sélectionner dans un menu déroulant les langues suivantes : fr-FR, en-GB, es-ES, de-DE, it-IT, nl-NL, pt-PT, ru-RU, eu-EU.
 * Se référer également à https://learn.microsoft.com/en-us/previous-versions/commerce-server/ee825488(v=cs.20)?redirectedfrom=MSDN pour voir les codes possibles.
 *
 * Couvert (langues disponibles dans ce projet via `WidgetLanguage`) :
 * - `fr` -> `fr-FR`
 * - `en` -> `en-GB`
 * - `es` -> `es-ES`
 * - `it` -> `it-IT`
 * - `de` -> `de-DE`
 * - `nl` -> `nl-NL`
 * - `ru` -> `ru-RU`
 * - `zh` -> `zh-CN`
 * - `pt-BR` -> `pt-BR`
 * - `ja` -> `ja-JP`
 * - `ko` -> `ko-KR`
 *
 * Non couvert (absent de `WidgetLanguage`) :
 * - `pt-PT` (il n'y a pas de `WidgetLanguage.PT_PT`)
 * - `eu-EU` (il n'y a pas de `WidgetLanguage.EU`)
 *
 * Fallback si jamais une valeur non mappée arrive : `fr-FR`.
 */
export function getEllohaCultureFromLanguage(language: WidgetLanguage): string {
  // `WidgetLanguage` est un `enum` TS (type-only ici), on garde le mapping en clés string tout en validant la complétude via le type.
  const map = {
    fr: "fr-FR",
    en: "en-GB",
    es: "es-ES",
    it: "it-IT",
    de: "de-DE",
    nl: "nl-NL",
    ru: "ru-RU",
    zh: "zh-CN",
    "pt-BR": "pt-BR",
    ja: "ja-JP",
    ko: "ko-KR",
  } as const satisfies Partial<Record<WidgetLanguage, string>>;

  return map[language] ?? "fr-FR";
}

/**
 * Récupère l'URL de l'image principale d'un item.
 * Si language est fourni, utilise la traduction correspondante (comme getIllustrationImageUrl).
 */
export function getImageUrl(
  item: ObjetTouristique,
  size: "liste" | "fiche" | "diaporama" | "original" = "liste",
  language?: string,
): string {
  const illustration = item.illustrations?.[0];
  return getIllustrationImageUrl(
    illustration as IllustrationLike | undefined,
    size,
    language,
  );
}

/**
 * Récupère l'URL d'une illustration pour une taille donnée (original, fiche, etc.).
 * Si language est fourni, utilise la traduction dont locale correspond (comme pour les multimédias), sinon la première.
 */
export function getIllustrationImageUrl(
  illustration: IllustrationLike | undefined,
  size: "liste" | "fiche" | "diaporama" | "original" = "original",
  language?: string,
): string {
  const fichiers = illustration?.traductionFichiers;
  if (!fichiers?.length) return "";
  const first = fichiers[0];
  const traduction = language
    ? fichiers.find((f) => f.locale === language) || first
    : first;
  switch (size) {
    case "liste":
      return traduction.urlListe || traduction.url || "";
    case "fiche":
      return traduction.urlFiche || traduction.url || "";
    case "diaporama":
      return traduction.urlDiaporama || traduction.url || "";
    case "original":
    default:
      return traduction.url || "";
  }
}

/**
 * Récupère le libellé traduit d'un objet multilingue
 */
export function getLabel(libelle: any, language: WidgetLanguage): string {
  return TranslationService.getLabelWithFallback(libelle, language);
}

/**
 * Traduit une clé de l'interface
 */
export function t(key: string, language: WidgetLanguage): string {
  return TranslationService.translate(key, language);
}

/**
 * Formate une date selon la langue
 */
export function formatDate(
  dateStr: string,
  language: WidgetLanguage,
  options?: Intl.DateTimeFormatOptions,
): string {
  return TranslationService.formatDate(dateStr, language, options);
}
